// Captures an import intent arriving via URL (?import=chrome) so the matching
// import dialog can be opened once the user is signed in and past the tour.
//
// Why this exists: the free Bookmarks HTML Viewer on the marketing site ends in
// a button labelled "Import into Linkflare", but it could only ever link to a
// bare /sign-up/ — so the promise did not land: the user arrived in an empty
// library and had to find the importer and re-drop the file unaided.
//
// Deliberately kept OUT of the signup-attribution string (signupReferrer.js):
// an import intent is not an acquisition source — that rides on `lf_from`
// alongside it. Per the attribution-capture lesson, a param that can arrive
// alone needs its own capture branch; this module is that branch.
//
// sessionStorage (not localStorage, which promoCode.js uses) on purpose: an
// import intent belongs to THIS signup journey. A promo code should survive a
// reader coming back next week; a re-opened import dialog a week later would
// just be a confusing popup. Same storage as signupReferrer.js, which is the
// proven carrier across a Clerk sign-up round trip.
import { IMPORT_SOURCES } from '../composables/useImportMenu.js'

const IMPORT_INTENT_KEY = 'linkflare_import_intent'

// Storage can be unavailable (private browsing, "Block All Cookies") — keep the
// captured intent in memory too so the deep link still works within the page
// session that saw the URL.
let memoryIntent = null

/**
 * Validates against the canonical source ids rather than a hand-kept copy, so a
 * newly added import source is deep-linkable the moment it joins the menu — and
 * a removed one stops being accepted. Fail-closed: anything not in the list
 * (typo, stale link, injected value) normalizes to null and is never stored.
 */
export function normalizeImportIntent(value) {
  if (typeof value !== 'string') return null
  const id = value.trim().toLowerCase()
  if (!id) return null
  return IMPORT_SOURCES.some(source => source.id === id) ? id : null
}

/**
 * Removes `import` from the address bar, preserving every other param.
 *
 * Done here at boot rather than through the router after the dialog opens: a
 * `router.replace` races the router's own initial navigation (it reads an empty
 * query and no-ops), and it can be superseded by the URL writes Bookmarks.vue
 * makes for its filters. Either way the param survived, so a reload re-captured
 * it and re-opened the dialog — the exact opposite of consume-once.
 *
 * ORDERING: `captureImportIntent()` runs from `main.js` at module scope, ABOVE
 * `createRouter` — earlier than `captureSignupReferrer` / `capturePromoCode`,
 * which run later inside `bootstrap()`. That is deliberate and load-bearing:
 * `createWebHistory()` snapshots the URL when it is constructed and the router
 * restores that snapshot, so stripping any later is undone. It is safe for the
 * two captures that follow because only the `import` key is removed, and
 * `bootstrapLocale` still finds `?lang` for the same reason.
 *
 * @param {URL} url a parsed copy of the current location, already in hand
 */
function stripImportParamFromUrl(url) {
  if (!url || !url.searchParams.has('import')) return
  try {
    url.searchParams.delete('import')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  } catch (e) {
    // No usable History API — the sessionStorage consume-once still holds
    // within this page session.
  }
}

/** Store a ?import= source if present; returns the effective stored intent, or null. */
export function captureImportIntent() {
  let intent = null
  let url = null
  try {
    url = new URL(window.location.href)
    intent = normalizeImportIntent(url.searchParams.get('import'))
  } catch (e) {
    // no usable URL — fall through to whatever is stored
  }
  // Strip whether or not the value was valid: an unknown source stores nothing,
  // but should not be left in the address bar to be shared or re-fired either.
  stripImportParamFromUrl(url)
  if (intent) {
    memoryIntent = intent
    try {
      sessionStorage.setItem(IMPORT_INTENT_KEY, intent)
    } catch (e) {
      // Private browsing / storage disabled — memoryIntent carries the session.
    }
    return intent
  }
  return peekImportIntent()
}

/** Read the stored intent without clearing it. */
export function peekImportIntent() {
  try {
    const raw = sessionStorage.getItem(IMPORT_INTENT_KEY)
    const stored = normalizeImportIntent(raw)
    if (stored) return stored
    // A value that no longer validates (a source retired from IMPORT_SOURCES, a
    // hand-edited key) is dead weight: drop it rather than leaving it to be read
    // by anything that skips normalize.
    if (raw !== null) sessionStorage.removeItem(IMPORT_INTENT_KEY)
  } catch (e) {
    // storage unavailable — fall through to memory
  }
  return memoryIntent
}

/**
 * Read and clear in one step. Consume-once is the point: the dialog opens on the
 * arrival that asked for it and never again, so a reload — or a later visit in
 * the same tab — does not reopen it.
 */
export function consumeImportIntent() {
  const intent = peekImportIntent()
  clearImportIntent()
  return intent
}

export function clearImportIntent() {
  memoryIntent = null
  try {
    sessionStorage.removeItem(IMPORT_INTENT_KEY)
  } catch (e) {
    // best-effort
  }
}
